// apps/admin/src/i18n/config.ts
//
// Phase 8.1 Wave 10 Task 46 — Admin i18n bootstrap.
//
// vg-binding: P8.D-165 — i18n EN/VI/FR with `admin.{module}.{page}.{element}`
//             namespace pattern. VI default per project rule.
// vg-binding: P8.D-155 — Error pages (404/500/403) consume i18n keys under
//             the `admin` namespace (`admin:errors.notFound.title` …).
//
// Design notes:
//   * The shared `@printway/shared/i18n` already wires `common` / `errors` /
//     `cookie` namespaces for vi/en/fr. This module layers an `admin`
//     namespace on top by passing a `resources` overlay into `createI18n()`.
//   * `react-i18next` is intentionally NOT introduced in this wave to keep
//     the admin bundle dep-graph stable. Instead we expose a tiny
//     `useTranslation()` hook (apps/admin/src/i18n/useTranslation.ts) that
//     subscribes to the underlying i18next `languageChanged` event.
//   * Locale persistence is delegated to the existing `usePreferencesStore`
//     (storage key follows existing patterns); this config only owns the
//     i18next instance lifecycle.
//
// SupportedLocale = 'vi' | 'en' | 'fr'   (mirrors @printway/shared).
// Default        = 'vi'.

import {
  createI18n,
  i18n,
  type I18nInstance,
  type SupportedLocale,
} from '@printway/shared/i18n';

import enAdmin from './locales/en/admin.json' with { type: 'json' };
import viAdmin from './locales/vi/admin.json' with { type: 'json' };
import frAdmin from './locales/fr/admin.json' with { type: 'json' };
// Phase 8.2 Wave 9 Task 56 — Navigation namespace (sidebar labels).
// Single source of truth for every sidebar entry across EN / VI / FR.
import enNav from './locales/en/navigation.json' with { type: 'json' };
import viNav from './locales/vi/navigation.json' with { type: 'json' };
import frNav from './locales/fr/navigation.json' with { type: 'json' };
// Phase 4.7 Wave 6 Task 19 — `shipping` namespace (vi/en/fr) for the
// /shipping admin surface. VI primary. Loaded eagerly so the
// 7-tab shell + sidebar entry resolve labels on first paint.
// vg-binding: CONTEXT:decisions=P4.7.D-24
import enShipping from './locales/en/shipping.json' with { type: 'json' };
import viShipping from './locales/vi/shipping.json' with { type: 'json' };
import frShipping from './locales/fr/shipping.json' with { type: 'json' };
// Phase 9.x i18n pilot — `users` namespace (vi/en/fr) for the /users admin
// page-area (list, detail, credit, sale/cs, impersonate). VI primary.
// Loaded eagerly (small bundle) so the users surface resolves on first paint.
import enUsers from './locales/en/users.json' with { type: 'json' };
import viUsers from './locales/vi/users.json' with { type: 'json' };
import frUsers from './locales/fr/users.json' with { type: 'json' };
// Phase 9.x i18n rollout — domain namespaces (vi/en/fr) for the support,
// finance and catalog admin page-areas. VI primary. Eagerly loaded.
import enSupport from './locales/en/support.json' with { type: 'json' };
import viSupport from './locales/vi/support.json' with { type: 'json' };
import frSupport from './locales/fr/support.json' with { type: 'json' };
import enFinance from './locales/en/finance.json' with { type: 'json' };
import viFinance from './locales/vi/finance.json' with { type: 'json' };
import frFinance from './locales/fr/finance.json' with { type: 'json' };
import enTemplateItems from './locales/en/templateItems.json' with { type: 'json' };
import viTemplateItems from './locales/vi/templateItems.json' with { type: 'json' };
import frTemplateItems from './locales/fr/templateItems.json' with { type: 'json' };
import enCatalog from './locales/en/catalog.json' with { type: 'json' };
import viCatalog from './locales/vi/catalog.json' with { type: 'json' };
import frCatalog from './locales/fr/catalog.json' with { type: 'json' };
import enTemplates from './locales/en/templates.json' with { type: 'json' };
import viTemplates from './locales/vi/templates.json' with { type: 'json' };
import frTemplates from './locales/fr/templates.json' with { type: 'json' };
// Phase 9.x i18n rollout batch 3 — domain namespaces for orders, vendors,
// sales, dashboard, merchants, stores page-areas. VI primary. Eagerly loaded.
import enOrders from './locales/en/orders.json' with { type: 'json' };
import viOrders from './locales/vi/orders.json' with { type: 'json' };
import frOrders from './locales/fr/orders.json' with { type: 'json' };
import enVendors from './locales/en/vendors.json' with { type: 'json' };
import viVendors from './locales/vi/vendors.json' with { type: 'json' };
import frVendors from './locales/fr/vendors.json' with { type: 'json' };
import enSales from './locales/en/sales.json' with { type: 'json' };
import viSales from './locales/vi/sales.json' with { type: 'json' };
import frSales from './locales/fr/sales.json' with { type: 'json' };
import enDashboard from './locales/en/dashboard.json' with { type: 'json' };
import viDashboard from './locales/vi/dashboard.json' with { type: 'json' };
import frDashboard from './locales/fr/dashboard.json' with { type: 'json' };
import enMerchants from './locales/en/merchants.json' with { type: 'json' };
import viMerchants from './locales/vi/merchants.json' with { type: 'json' };
import frMerchants from './locales/fr/merchants.json' with { type: 'json' };
import enStores from './locales/en/stores.json' with { type: 'json' };
import viStores from './locales/vi/stores.json' with { type: 'json' };
import frStores from './locales/fr/stores.json' with { type: 'json' };
// Phase 9.x i18n rollout batch 3B — settings, notifications, home, affiliate,
// brands, pricing, shipments, ops page-areas. VI primary. Eagerly loaded.
import enSettings from './locales/en/settings.json' with { type: 'json' };
import viSettings from './locales/vi/settings.json' with { type: 'json' };
import frSettings from './locales/fr/settings.json' with { type: 'json' };
import enNotifications from './locales/en/notifications.json' with { type: 'json' };
import viNotifications from './locales/vi/notifications.json' with { type: 'json' };
import frNotifications from './locales/fr/notifications.json' with { type: 'json' };
import enHome from './locales/en/home.json' with { type: 'json' };
import viHome from './locales/vi/home.json' with { type: 'json' };
import frHome from './locales/fr/home.json' with { type: 'json' };
import enAffiliate from './locales/en/affiliate.json' with { type: 'json' };
import viAffiliate from './locales/vi/affiliate.json' with { type: 'json' };
import frAffiliate from './locales/fr/affiliate.json' with { type: 'json' };
import enBrands from './locales/en/brands.json' with { type: 'json' };
import viBrands from './locales/vi/brands.json' with { type: 'json' };
import frBrands from './locales/fr/brands.json' with { type: 'json' };
import enPricing from './locales/en/pricing.json' with { type: 'json' };
import viPricing from './locales/vi/pricing.json' with { type: 'json' };
import frPricing from './locales/fr/pricing.json' with { type: 'json' };
import enShipments from './locales/en/shipments.json' with { type: 'json' };
import viShipments from './locales/vi/shipments.json' with { type: 'json' };
import frShipments from './locales/fr/shipments.json' with { type: 'json' };
import enOps from './locales/en/ops.json' with { type: 'json' };
import viOps from './locales/vi/ops.json' with { type: 'json' };
import frOps from './locales/fr/ops.json' with { type: 'json' };
// Ticket multi-channel notifications (Plan 4) — `notifChannels` namespace
// (vi/en/fr) for the Profile → Notifications channel-connect section
// (Lark / Telegram). VI primary. Eagerly loaded.
import enNotifChannels from './locales/en/notifChannels.json' with { type: 'json' };
import viNotifChannels from './locales/vi/notifChannels.json' with { type: 'json' };
import frNotifChannels from './locales/fr/notifChannels.json' with { type: 'json' };
// Phase 10.2 — `salesOperations` namespace (vi/en/fr) for the
// /sales/operations analytics surface (Dashboard / Comparison / Forecast).
// VI primary. Eagerly loaded so the 3-tab shell + freshness banner + card
// states resolve labels on first paint.
import enSalesOperations from './locales/en/salesOperations.json' with { type: 'json' };
import viSalesOperations from './locales/vi/salesOperations.json' with { type: 'json' };
import frSalesOperations from './locales/fr/salesOperations.json' with { type: 'json' };
// Phase 4.4.2 Wave 3 Task 12 — `vendorAllocations` namespace (vi/en/fr) for the
// /vendors/allocations surface (Stats / Configs / Proposals tabs + dialogs +
// vendor chip + 9 domain error codes). VI primary. Eagerly loaded so the
// 3-tab shell + sidebar entry + toast copy resolve on first paint.
// vg-binding: CONTEXT:decisions=P4.4.2.D-13,P4.4.2.D-14,P4.4.2.D-15
// vg-binding: INTERFACE-STANDARDS:error-shape
import enVendorAllocations from './locales/en/vendorAllocations.json' with { type: 'json' };
import viVendorAllocations from './locales/vi/vendorAllocations.json' with { type: 'json' };
import frVendorAllocations from './locales/fr/vendorAllocations.json' with { type: 'json' };
// Phase 17 Wave 8 Task 28 — `vendorRouting` namespace (vi/en/fr) for the
// /vendors/routing surface (Weights / Merchant rules / Preferences / Fence
// report tabs + dialogs + the pending-reason and drift vocabularies + one
// entry per typed API error code so `t('errors.' + error.code)` resolves).
// VI primary. Eagerly loaded so the 4-tab shell, the sidebar leaf
// (`vendorRouting:nav.routing`) and the toast copy resolve on first paint.
// vg-binding: CONTEXT:decisions=P17.D-25,P17.D-26,P17.D-33,P17.D-41,P17.D-42,P17.D-45,P17.D-47
// vg-binding: INTERFACE-STANDARDS:error-shape
import enVendorRouting from './locales/en/vendorRouting.json' with { type: 'json' };
import viVendorRouting from './locales/vi/vendorRouting.json' with { type: 'json' };
import frVendorRouting from './locales/fr/vendorRouting.json' with { type: 'json' };

/** Locales shipped with the admin portal. Aligned with shared/i18n. */
export const ADMIN_SUPPORTED_LOCALES = ['vi', 'en', 'fr'] as const;

/** Default UI locale per project rule (Printway is VN-first). */
export const ADMIN_DEFAULT_LOCALE: SupportedLocale = 'vi';

/** Localised date format hints — consumed by Intl.DateTimeFormat callers. */
export const ADMIN_DATE_FORMAT: Readonly<Record<SupportedLocale, string>> = {
  vi: 'DD/MM/YYYY HH:mm',
  en: 'MM/DD/YYYY HH:mm',
  fr: 'DD/MM/YYYY HH:mm',
};

/**
 * Browser language detector (lightweight, no extra plugin). Falls back to the
 * VI default when `localStorage` is unavailable (SSR/JSDOM) or the persisted
 * locale is not in the supported set.
 */
export function detectInitialLocale(): SupportedLocale {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return ADMIN_DEFAULT_LOCALE;
  }

  // 1. Honour any explicitly persisted preference.
  const persisted = localStorage.getItem('admin.locale');
  if (
    persisted === 'vi' ||
    persisted === 'en' ||
    persisted === 'fr'
  ) {
    return persisted;
  }

  // 2. Sniff the browser locale (first 2 chars only — region codes ignored).
  const nav =
    typeof navigator !== 'undefined' && typeof navigator.language === 'string'
      ? navigator.language.slice(0, 2).toLowerCase()
      : '';
  if (nav === 'vi' || nav === 'en' || nav === 'fr') {
    return nav;
  }

  return ADMIN_DEFAULT_LOCALE;
}

/**
 * Initialize the admin i18n instance. Idempotent: the first caller wins; any
 * subsequent call (e.g. HMR re-mount) returns the cached promise.
 */
let initPromise: Promise<I18nInstance> | null = null;
export function initAdminI18n(
  lng: SupportedLocale = detectInitialLocale(),
): Promise<I18nInstance> {
  if (initPromise) {
    return initPromise;
  }

  initPromise = createI18n({
    lng,
    defaultNS: 'common',
    // Eagerly load both shared + admin namespaces — error pages must render
    // synchronously when the router falls through to the 404/500/403 routes.
    // Phase 8.2 Wave 9 Task 56 — `navigation` namespace owns sidebar labels.
    ns: ['common', 'errors', 'admin', 'navigation', 'shipping', 'users', 'support', 'finance', 'catalog', 'templates', 'orders', 'vendors', 'sales', 'dashboard', 'merchants', 'stores', 'settings', 'notifications', 'home', 'affiliate', 'brands', 'pricing', 'shipments', 'ops', 'notifChannels', 'salesOperations', 'vendorAllocations', 'vendorRouting', 'templateItems'],
    resources: {
      en: { admin: enAdmin, navigation: enNav, shipping: enShipping, users: enUsers, support: enSupport, finance: enFinance, catalog: enCatalog, templates: enTemplates, orders: enOrders, vendors: enVendors, sales: enSales, dashboard: enDashboard, merchants: enMerchants, stores: enStores, settings: enSettings, notifications: enNotifications, home: enHome, affiliate: enAffiliate, brands: enBrands, pricing: enPricing, shipments: enShipments, ops: enOps, notifChannels: enNotifChannels, salesOperations: enSalesOperations, vendorAllocations: enVendorAllocations, vendorRouting: enVendorRouting, templateItems: enTemplateItems },
      vi: { admin: viAdmin, navigation: viNav, shipping: viShipping, users: viUsers, support: viSupport, finance: viFinance, catalog: viCatalog, templates: viTemplates, orders: viOrders, vendors: viVendors, sales: viSales, dashboard: viDashboard, merchants: viMerchants, stores: viStores, settings: viSettings, notifications: viNotifications, home: viHome, affiliate: viAffiliate, brands: viBrands, pricing: viPricing, shipments: viShipments, ops: viOps, notifChannels: viNotifChannels, salesOperations: viSalesOperations, vendorAllocations: viVendorAllocations, vendorRouting: viVendorRouting, templateItems: viTemplateItems },
      fr: { admin: frAdmin, navigation: frNav, shipping: frShipping, users: frUsers, support: frSupport, finance: frFinance, catalog: frCatalog, templates: frTemplates, orders: frOrders, vendors: frVendors, sales: frSales, dashboard: frDashboard, merchants: frMerchants, stores: frStores, settings: frSettings, notifications: frNotifications, home: frHome, affiliate: frAffiliate, brands: frBrands, pricing: frPricing, shipments: frShipments, ops: frOps, notifChannels: frNotifChannels, salesOperations: frSalesOperations, vendorAllocations: frVendorAllocations, vendorRouting: frVendorRouting, templateItems: frTemplateItems },
    },
  });

  return initPromise;
}

/**
 * Change the active locale and persist the choice. Triggers an i18next
 * `languageChanged` event which the `useTranslation` hook listens to.
 */
export async function changeAdminLocale(
  next: SupportedLocale,
): Promise<void> {
  await i18n.changeLanguage(next);
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem('admin.locale', next);
    } catch {
      // Quota / private-mode failures must not break locale switching.
    }
  }
}

/** Re-export the shared instance for callers that need direct access. */
export { i18n };
export type { SupportedLocale };
